import React from "react";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import styles from "../authorization.module.scss";
import { EmailIcon, LockIcon, EyeIcon, GoogleIcon } from "../AuthIcons";

const cx = classNames.bind(styles);

const isGoogleOAuthEnabled = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === "true";

interface SignInViewProps {
  email: string;
  password: string;
  rememberMe: boolean;
  showPassword: boolean;
  emailError: boolean;
  passwordError: boolean;
  authError: string | null;
  isLoading: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onTogglePassword: () => void;
  onForgotPassword: () => void;
  onSwitchToSignUp: () => void;
  onGoogleSignIn: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export const SignInView: React.FC<SignInViewProps> = ({
  email,
  password,
  rememberMe,
  showPassword,
  emailError,
  passwordError,
  authError,
  isLoading,
  onEmailChange,
  onPasswordChange,
  onRememberMeChange,
  onTogglePassword,
  onForgotPassword,
  onSwitchToSignUp,
  onGoogleSignIn,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className={cx('form')} onSubmit={onSubmit}>
      <div className={cx('formHeader')}>
        <h2 className={cx('formTitle')}>{t("signIn")}</h2>
        <p className={cx('formSubtitle')}>{t("enterCredentials")}</p>
      </div>

      {authError && <div className={cx('errorMessage')}>{t(authError)}</div>}

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("email")}</label>
        <div className={cx('inputWrapper', { inputError: emailError })}>
          <div className={cx('inputIcon')}>
            <EmailIcon />
          </div>
          <input
            name="email"
            type="email"
            className={cx('input')}
            placeholder={t("enterEmail")}
            value={email}
            autoComplete="username"
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </div>
        {emailError && <span className={cx('inputErrorText')}>{t("enterEmail")}</span>}
      </div>

      <div className={cx('inputGroup')}>
        <div className={cx('inputLabelRow')}>
          <label className={cx('inputLabel')}>{t("password")}</label>
          <button
            type="button"
            onClick={onForgotPassword}
            className={cx('forgotLink')}
            tabIndex={-1}
          >
            {t("forgotPassword")}
          </button>
        </div>
        <div className={cx('inputWrapper', { inputError: passwordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("enterPassword")}
            value={password}
            autoComplete="current-password"
            onChange={(e) => onPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onTogglePassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>
        {passwordError && <span className={cx('inputErrorText')}>{t("enterPassword")}</span>}
      </div>

      <div className={cx('checkboxGroup')}>
        <label className={cx('checkboxLabel')}>
          <input
            type="checkbox"
            className={cx('checkbox')}
            checked={rememberMe}
            onChange={(e) => onRememberMeChange(e.target.checked)}
          />
          <span className={cx('checkboxCustom')}></span>
          <span className={cx('checkboxText')}>{t("rememberMe")}</span>
        </label>
      </div>

      <button type="submit" className={cx('submitButton')} disabled={isLoading}>
        {isLoading ? <span className={cx('spinner')}></span> : t("signIn")}
      </button>

      {isGoogleOAuthEnabled && (
        <>
          <div className={cx('divider')}>
            <span>{t("or")}</span>
          </div>

          <button
            type="button"
            className={cx('googleButton')}
            onClick={onGoogleSignIn}
            disabled={isLoading}
          >
            <GoogleIcon />
            <span>{t("signInWithGoogle")}</span>
          </button>
        </>
      )}

      <div className={cx('formFooter')}>
        <span className={cx('footerText')}>{t("dontHaveAccount")}</span>
        <button
          type="button"
          onClick={onSwitchToSignUp}
          className={cx('switchButton')}
        >
          {t("signUp")}
        </button>
      </div>
    </form>
  );
};
